import { apiClient } from "./client";
import type {
    CampaignAudienceDetailResponse,
    CampaignAudiencePreview,
    CampaignAudienceRow,
    CampaignsDatasetResponse,
    CampaignTemplateRow,
    MarketingCampaignRow,
} from "./types";

export async function fetchCampaignsDataset(periodDays: 7 | 30 | 90 = 30) {
    const response = await apiClient.get<CampaignsDatasetResponse>("/campaigns", {
        params: {
            period: periodDays,
        },
    });

    return response.data;
}

export async function createCampaignTemplate(payload: {
    name: string;
    channel: string;
    purpose: string;
    campaignType: string;
    season?: string;
    productGroup?: string;
    subject: string;
    content: string;
    contentDocument?: Record<string, unknown> | null;
    tags?: string[];
    language?: string;
}) {
    const response = await apiClient.post<{ template: CampaignTemplateRow }>("/campaigns/templates", payload);

    return response.data;
}

export async function updateCampaignTemplate(templateId: number, payload: {
    name: string;
    channel: string;
    purpose: string;
    campaignType: string;
    season?: string;
    productGroup?: string;
    subject: string;
    content: string;
    contentDocument?: Record<string, unknown> | null;
    tags?: string[];
    language?: string;
}) {
    const response = await apiClient.patch<{ template: CampaignTemplateRow }>(`/campaigns/templates/${templateId}`, payload);

    return response.data;
}

export async function restoreCampaignTemplateVersion(templateId: number, version: number) {
    const response = await apiClient.post<{ template: CampaignTemplateRow }>(`/campaigns/templates/${templateId}/restore`, { version });

    return response.data;
}

export async function previewCampaignAudience(payload: {
    name: string;
    description: string;
    segmentType?: string;
    filter?: Record<string, unknown>;
    members?: Array<{ email?: string }>;
}) {
    const response = await apiClient.post<CampaignAudiencePreview>("/campaigns/audiences/preview", payload);

    return response.data;
}

export async function refreshCampaignAudience(audienceId: number) {
    const response = await apiClient.post<{ audience: CampaignAudienceRow }>(`/campaigns/audiences/${audienceId}/refresh`);

    return response.data;
}

export async function fetchCampaignAudience(audienceId: number) {
    const response = await apiClient.get<CampaignAudienceDetailResponse>(`/campaigns/audiences/${audienceId}`);

    return response.data;
}

export async function updateCampaignAudience(audienceId: number, payload: {
    name: string;
    description?: string;
    channel?: string;
    segmentType?: string;
    filter?: Record<string, unknown>;
    members?: Array<{
        email?: string;
        partnerId?: number;
        profileId?: string;
        displayName?: string;
        phoneNormalized?: string;
    }>;
}) {
    const response = await apiClient.patch<{ audience: CampaignAudienceRow }>(`/campaigns/audiences/${audienceId}`, payload);

    return response.data;
}

export async function deleteCampaignAudience(audienceId: number) {
    const response = await apiClient.delete<{ success: boolean }>(`/campaigns/audiences/${audienceId}`);

    return response.data;
}

export async function createCampaignAudience(payload: {
    name: string;
    description?: string;
    channel?: string;
    segmentType?: string;
    filter?: Record<string, unknown>;
    members?: Array<{
        email?: string;
        partnerId?: number;
        profileId?: string;
        displayName?: string;
        phoneNormalized?: string;
    }>;
}) {
    const response = await apiClient.post<{ audience: CampaignAudienceRow }>("/campaigns/audiences", payload);

    return response.data;
}

export async function createMarketingCampaign(payload: {
    name: string;
    channel?: string;
    audienceId?: number | null;
    audienceName?: string;
    audienceSize?: number;
    templateId?: number | null;
    scheduleAt: string;
    timeZone: string;
    frequencyCapHours: number;
    quietHours?: { from?: string; to?: string };
    abTesting?: { enabled?: boolean; splitA?: number; splitB?: number };
    attribution?: { utmSource?: string; utmMedium?: string; utmCampaign?: string };
}) {
    const response = await apiClient.post<{ campaign: MarketingCampaignRow }>("/campaigns", payload);

    return response.data;
}

export async function updateMarketingCampaign(campaignId: number, payload: Parameters<typeof createMarketingCampaign>[0]) {
    const response = await apiClient.patch<{ campaign: MarketingCampaignRow }>(`/campaigns/${campaignId}`, payload);

    return response.data;
}

export async function deleteMarketingCampaign(campaignId: number) {
    const response = await apiClient.delete<{ success: boolean }>(`/campaigns/${campaignId}`);

    return response.data;
}

export async function deleteCampaignTemplate(templateId: number) {
    const response = await apiClient.delete<{ success: boolean }>(`/campaigns/templates/${templateId}`);

    return response.data;
}

export async function sendCampaignTest(payload: { campaignId: number; emails: string[] }) {
    const response = await apiClient.post<{ success: boolean; sent: number; recipients: string[] }>("/campaigns/test", payload);

    return response.data;
}

export async function activateCampaign(payload: { campaignId: number }) {
    const response = await apiClient.post<{ campaign: MarketingCampaignRow }>("/campaigns/activate", payload);

    return response.data;
}

